/**
 * 节点设置 · Agent 卡片（C5 + E3）：上报信息展示 + 安装/卸载命令（复制粘贴）
 * + 远程升级（发布清单 → POST /agent/upgrade，Agent 拉取式自重启，替换升级命令）。
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { hubStateQueryOptions } from '../../data/queries';
import { fetchAgentReleases, upgradeAgentRemote, type AgentRelease } from '../../data/agent';
import { action, actionLabel, type ActionRef } from '../../registry/actions';
import { ConfirmAction } from '../../patterns';
import {
  Badge,
  Banner,
  Button,
  Card,
  Drawer,
  KeyValue,
  Row,
  Skeleton,
  Stack,
  useToast,
} from '../../ui';
import { zh, fmt } from '../../i18n/zh';
import { formatAbsoluteTime, shortFingerprint } from '../../i18n/format';
import { ApiError } from '../../data/client';
import { AgentCommandDrawer } from './AgentCommandDrawer';

function agentModeLabel(mode: 'wg' | 'caddy' | undefined): string {
  if (mode === 'wg') return zh.agent.agentModeWg;
  if (mode === 'caddy') return zh.agent.agentModeCaddy;
  return zh.agent.agentModeUnknown;
}

export function AgentCard({
  hubId,
  hubName,
  lastSeenAt,
  offline,
}: {
  hubId: string;
  hubName: string;
  lastSeenAt: number | null;
  offline: boolean;
}) {
  const stateQuery = useQuery(hubStateQueryOptions(hubId));
  const toast = useToast();
  const [drawerKind, setDrawerKind] = useState<'install' | 'uninstall' | null>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [releases, setReleases] = useState<AgentRelease[] | null>(null);
  const [dispatched, setDispatched] = useState(false);
  const [upgradeError, setUpgradeError] = useState<string | null>(null);
  const agent = stateQuery.data?.state?.agent;
  const version = agent?.version ?? null;
  const stateOffline = offline || stateQuery.data?.reachable === false;
  const latest = releases?.[0] ?? null;

  // 发布清单（#32）：远程升级从中取包地址与 sha256。
  useEffect(() => {
    let cancelled = false;
    void fetchAgentReleases()
      .then((items) => {
        if (!cancelled) setReleases(items);
      })
      .catch(() => {
        if (!cancelled) setReleases([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const confirmRemoteUpgrade = async () => {
    if (!latest) return;
    try {
      await upgradeAgentRemote(hubId, { packageUrl: latest.url, sha256: latest.sha256 });
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.message
          : error instanceof Error
            ? error.message
            : zh.agent.remoteUpgradeFailed;
      setUpgradeError(message);
      throw error;
    }
    setUpgradeOpen(false);
    setUpgradeError(null);
    setDispatched(true);
    toast.show({ message: zh.agent.remoteUpgradeDispatched });
  };

  const commandButtons: { kind: 'install' | 'uninstall'; ref: ActionRef }[] = [
    { kind: 'install', ref: action('agent.installCommand') },
    { kind: 'uninstall', ref: action('agent.uninstallCommand') },
  ];

  return (
    <>
      <Card title={zh.agent.title}>
        <Stack gap={3}>
          {stateQuery.isLoading && !stateQuery.data ? (
            <Stack gap={2}>
              <Skeleton height={10} width="35%" />
              <Skeleton height={10} width="55%" />
            </Stack>
          ) : null}

          {stateQuery.error ? (
            <Banner
              tone="danger"
              title={zh.agent.loadError}
              description={stateQuery.error instanceof Error ? stateQuery.error.message : undefined}
              action={
                <Button
                  label={zh.common.retry}
                  size="sm"
                  onClick={() => void stateQuery.refetch()}
                />
              }
            />
          ) : null}
          {stateOffline ? (
            <Banner tone="neutral" title={zh.agent.offline} description={zh.agent.offlineHint} />
          ) : null}

          {agent ? (
            <>
              <Row gap={2} wrap>
                <Badge tone="ok" label={version ? `v${version}` : zh.agent.notReported} />
              </Row>
              <KeyValue
                items={[
                  { key: zh.agent.version, value: version ?? zh.agent.notReported, mono: true },
                  { key: zh.agent.runMode, value: agentModeLabel(agent.mode) },
                  {
                    key: zh.agent.lastSeen,
                    value: lastSeenAt === null ? '—' : formatAbsoluteTime(lastSeenAt),
                  },
                ]}
              />
            </>
          ) : stateQuery.data ? (
            <Banner tone="neutral" title={zh.agent.noAgentHint} />
          ) : null}

          {dispatched && version !== latest?.version ? (
            <Banner tone="info" title={zh.agent.remoteUpgradeDispatched} />
          ) : null}
          {upgradeError ? <Banner tone="danger" title={upgradeError} /> : null}

          <Row gap={2} wrap>
            {commandButtons.map((item) => (
              <Button
                key={item.kind}
                label={actionLabel(item.ref)}
                icon={item.kind === 'install' ? 'download' : 'x'}
                size="sm"
                disabled={stateOffline && item.kind !== 'install'}
                disabledReason={
                  stateOffline && item.kind !== 'install' ? zh.agent.disabledOffline : undefined
                }
                onClick={() => setDrawerKind(item.kind)}
              />
            ))}
            <Button
              label={actionLabel(action('agent.remoteUpgrade'))}
              icon="refresh"
              size="sm"
              variant="primary"
              disabled={stateOffline || !latest}
              disabledReason={
                stateOffline
                  ? zh.agent.disabledOffline
                  : !latest
                    ? zh.agent.remoteUpgradeNoRelease
                    : undefined
              }
              onClick={() => {
                setUpgradeError(null);
                setUpgradeOpen(true);
              }}
            />
          </Row>
        </Stack>
      </Card>

      {drawerKind ? (
        <AgentCommandDrawer
          open
          kind={drawerKind}
          hubId={hubId}
          hubName={hubName}
          offline={stateOffline}
          onClose={() => setDrawerKind(null)}
        />
      ) : null}

      {upgradeOpen && latest ? (
        <Drawer
          open
          title={zh.agent.remoteUpgradeTitle}
          context={fmt(zh.agent.drawerContext, { hub: hubName })}
          onClose={() => setUpgradeOpen(false)}
          footer={<Button label={zh.common.close} onClick={() => setUpgradeOpen(false)} />}
        >
          <Stack gap={3}>
            <KeyValue
              items={[
                { key: zh.agent.version, value: version ?? zh.agent.notReported, mono: true },
                { key: zh.agent.remoteUpgradeLatest, value: latest.version, mono: true },
                { key: zh.agent.remoteUpgradePackage, value: latest.url, mono: true },
                { key: zh.agent.remoteUpgradeSha, value: shortFingerprint(latest.sha256), mono: true },
              ]}
            />
            {upgradeError ? <Banner tone="danger" title={upgradeError} /> : null}
            <ConfirmAction
              open
              action={action('agent.remoteUpgrade')}
              title={zh.agent.remoteUpgradeTitle}
              body={zh.agent.remoteUpgradeBody}
              confirmLabel={zh.agent.remoteUpgradeConfirm}
              targetName={latest.version}
              nameLabel={fmt(zh.agent.remoteUpgradeNameLabel, { version: latest.version })}
              onCancel={() => setUpgradeOpen(false)}
              onConfirm={confirmRemoteUpgrade}
            />
          </Stack>
        </Drawer>
      ) : null}
    </>
  );
}
